// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface CollectionItem {
  _id: string;
  name: string;
  color?: string;
  itemCount?: number;
}

interface DiscoveredFeed {
  url: string;
  title: string;
  type?: string;
}

interface AddFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: (data: {
    feedUrl: string;
    title?: string;
    refreshIntervalMinutes?: number;
    collectionIds?: string[];
  }) => Promise<any>;
  onDiscover: (url: string) => Promise<DiscoveredFeed[]>;
  collections?: CollectionItem[];
  defaultCollectionId?: string;
  onOpenCreateCollection?: () => void;
  onCreateCollection?: (data: { name: string; description?: string; color?: string }) => Promise<any>;
  justCreatedCollectionId?: string | null;
}

export default function AddFeedModal({
  isOpen,
  onClose,
  onSubscribe,
  onDiscover,
  collections = [],
  defaultCollectionId,
  onOpenCreateCollection,
  onCreateCollection,
  justCreatedCollectionId,
}: AddFeedModalProps) {
  const [urlInput, setUrlInput] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [discoveredFeeds, setDiscoveredFeeds] = useState<DiscoveredFeed[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Inline collection creation state
  const [isCreatingInline, setIsCreatingInline] = useState(false);
  const [inlineCollectionName, setInlineCollectionName] = useState('');
  const [isInlineSubmitting, setIsInlineSubmitting] = useState(false);
  const [collectionFeedback, setCollectionFeedback] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (defaultCollectionId) {
        setSelectedCollectionIds([defaultCollectionId]);
      } else {
        setSelectedCollectionIds([]);
      }
      setErrorMessage('');
      setDiscoveredFeeds([]);
      setIsCreatingInline(false);
      setInlineCollectionName('');
      setCollectionFeedback('');
    }
  }, [isOpen, defaultCollectionId]);

  useEffect(() => {
    if (justCreatedCollectionId) {
      setSelectedCollectionIds((prev) =>
        prev.includes(justCreatedCollectionId) ? prev : [...prev, justCreatedCollectionId]
      );
    }
  }, [justCreatedCollectionId]);

  if (!isOpen) return null;

  const handleToggleCollection = (colId: string) => {
    setSelectedCollectionIds((prev) =>
      prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]
    );
  };

  const handleSelectAllCollections = () => {
    setSelectedCollectionIds(collections.map((c) => c._id));
  };

  const handleClearCollections = () => {
    setSelectedCollectionIds([]);
  };

  const handleInlineAddCollection = async () => {
    const trimmed = inlineCollectionName.trim();
    if (!trimmed) return;

    // Check if collection with this name already exists in collections (case-insensitive)
    const existing = collections.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (existing) {
      setSelectedCollectionIds((prev) =>
        prev.includes(existing._id) ? prev : [...prev, existing._id]
      );
      setInlineCollectionName('');
      setIsCreatingInline(false);
      setCollectionFeedback(`Selected existing "${existing.name}" collection`);
      setTimeout(() => setCollectionFeedback(''), 4000);
      return;
    }

    if (onCreateCollection) {
      setIsInlineSubmitting(true);
      try {
        const created = await onCreateCollection({ name: trimmed });
        const createdId = created?._id || created?.data?._id;
        if (createdId) {
          setSelectedCollectionIds((prev) =>
            prev.includes(createdId) ? prev : [...prev, createdId]
          );
        }
        setInlineCollectionName('');
        setIsCreatingInline(false);
        setCollectionFeedback(`Added and selected "${trimmed}"`);
        setTimeout(() => setCollectionFeedback(''), 4000);
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to create collection');
      } finally {
        setIsInlineSubmitting(false);
      }
    } else if (onOpenCreateCollection) {
      onOpenCreateCollection();
    }
  };

  const handleDiscoverOrSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    setErrorMessage('');
    const target = urlInput.trim();

    // If it looks like a direct xml/rss feed, subscribe directly
    if (target.endsWith('.xml') || target.endsWith('/feed') || target.endsWith('/rss')) {
      handleDirectSubscribe(target);
      return;
    }

    // Otherwise discover feeds from page
    setIsDiscovering(true);
    try {
      const feeds = await onDiscover(target);
      if (feeds && feeds.length > 0) {
        setDiscoveredFeeds(feeds);
      } else {
        // Fallback to subscribing directly
        await handleDirectSubscribe(target);
      }
    } catch (err: any) {
      // If discovery failed, try direct subscribe
      try {
        await handleDirectSubscribe(target);
      } catch (subErr: any) {
        setErrorMessage(subErr.message || 'Could not find a valid RSS/Atom feed at this address.');
      }
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleDirectSubscribe = async (feedUrl: string, title?: string) => {
    setIsSubscribing(true);
    setErrorMessage('');
    try {
      await onSubscribe({
        feedUrl,
        title: customTitle || title,
        refreshIntervalMinutes: refreshInterval,
        collectionIds: selectedCollectionIds,
      });
      setUrlInput('');
      setCustomTitle('');
      setDiscoveredFeeds([]);
      setSelectedCollectionIds([]);
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to subscribe to feed.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" style={{ maxWidth: '580px' }} onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'var(--reader-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--reader-accent)',
              }}
            >
              <i className="fi fi-rr-rss" style={{ fontSize: '0.85rem' }} />
            </div>
            <h3 className="reader-modal-title">Subscribe to RSS / Atom Feed</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--reader-text-muted)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleDiscoverOrSubmit}>
          <div className="reader-modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}
              >
                {errorMessage}
              </div>
            )}

            <div className="reader-form-group">
              <label className="reader-form-label">Feed or Website URL *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="url"
                  className="reader-input"
                  placeholder="https://techcrunch.com, https://daringfireball.net/feeds/json..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  style={{ flex: 1 }}
                  autoFocus
                  required
                  disabled={isDiscovering || isSubscribing}
                />
                <button
                  type="submit"
                  className="reader-btn"
                  disabled={isDiscovering || isSubscribing || !urlInput.trim()}
                >
                  {isDiscovering ? 'Discovering…' : 'Find Feed'}
                </button>
              </div>
              <span style={{ fontSize: '0.74rem', color: 'var(--reader-text-subtle)' }}>
                You can enter a direct RSS feed URL or any website URL. We will automatically detect available feeds.
              </span>
            </div>

            {/* Discovered Feeds List */}
            {discoveredFeeds.length > 0 && (
              <div className="reader-form-group">
                <label className="reader-form-label">Available Feeds Found:</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {discoveredFeeds.map((feed, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid var(--reader-border)',
                        borderRadius: '6px',
                        background: 'var(--reader-hover)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{feed.title}</div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--reader-text-subtle)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {feed.url}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="reader-btn reader-btn-primary"
                        style={{ fontSize: '0.78rem', padding: '3px 10px', height: '28px' }}
                        disabled={isSubscribing}
                        onClick={() => handleDirectSubscribe(feed.url, feed.title)}
                      >
                        Subscribe
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* OPTIONAL COLLECTIONS SELECTION */}
            <div className="reader-form-group">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '2px',
                }}
              >
                <div>
                  <label className="reader-form-label" style={{ display: 'block' }}>
                    Add to Collection (Optional)
                  </label>
                  <span
                    style={{
                      fontSize: '0.74rem',
                      color: 'var(--reader-text-subtle)',
                      display: 'block',
                    }}
                  >
                    Optionally organize this feed into one or more collections, or leave unselected.
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {collections.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handleSelectAllCollections}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                        }}
                      >
                        Select all
                      </button>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                    </>
                  )}
                  {selectedCollectionIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearCollections}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '0.72rem',
                        color: 'var(--reader-text-muted)',
                        cursor: 'pointer',
                        padding: '2px 4px',
                      }}
                    >
                      Clear
                    </button>
                  )}
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingInline((prev) => !prev);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                        <span>New</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {collections.length === 0 ? (
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px dashed var(--reader-border)',
                    background: 'var(--reader-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: '0.78rem', color: 'var(--reader-text-muted)' }}>
                    No collections created yet. (Optional)
                  </span>
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ fontSize: '0.72rem', height: '24px', padding: '0 8px' }}
                      onClick={() => setIsCreatingInline(true)}
                    >
                      <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                      <span>Create Collection</span>
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
                    gap: '8px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    padding: '4px 1px',
                  }}
                >
                  {collections.map((col) => {
                    const isSelected = selectedCollectionIds.includes(col._id);
                    return (
                      <div
                        key={col._id}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => handleToggleCollection(col._id)}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            handleToggleCollection(col._id);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '7px 9px',
                          borderRadius: '6px',
                          border: isSelected
                            ? '1.5px solid var(--reader-accent)'
                            : '1px solid var(--reader-border)',
                          background: isSelected
                            ? 'var(--reader-accent-light)'
                            : 'var(--reader-bg)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            border: isSelected
                              ? '1.5px solid var(--reader-accent)'
                              : '1.5px solid var(--reader-text-subtle)',
                            background: isSelected ? 'var(--reader-accent)' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '0.65rem',
                            flexShrink: 0,
                          }}
                        >
                          {isSelected && <i className="fi fi-rr-check" />}
                        </div>

                        <span
                          style={{
                            width: '9px',
                            height: '9px',
                            borderRadius: '2px',
                            backgroundColor: col.color || 'var(--reader-accent)',
                            flexShrink: 0,
                          }}
                        />

                        <span
                          style={{
                            fontSize: '0.8rem',
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? 'var(--reader-accent)' : 'var(--reader-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                          title={col.name}
                        >
                          {col.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCreatingInline && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    background: 'var(--reader-hover)',
                    border: '1px solid var(--reader-border)',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="reader-input"
                      style={{ flex: 1, height: '30px', fontSize: '0.82rem', padding: '4px 8px' }}
                      placeholder="Collection name (e.g. Technology)..."
                      value={inlineCollectionName}
                      onChange={(e) => setInlineCollectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleInlineAddCollection();
                        } else if (e.key === 'Escape') {
                          setIsCreatingInline(false);
                          setInlineCollectionName('');
                        }
                      }}
                      autoFocus
                      disabled={isInlineSubmitting}
                    />
                    <button
                      type="button"
                      className="reader-btn reader-btn-primary"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 10px' }}
                      onClick={handleInlineAddCollection}
                      disabled={isInlineSubmitting || !inlineCollectionName.trim()}
                    >
                      {isInlineSubmitting ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 8px' }}
                      onClick={() => {
                        setIsCreatingInline(false);
                        setInlineCollectionName('');
                      }}
                      disabled={isInlineSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.71rem', color: 'var(--reader-text-subtle)' }}>
                      Enter a new or existing collection name.
                    </span>
                    {onOpenCreateCollection && (
                      <button
                        type="button"
                        onClick={onOpenCreateCollection}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.71rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        More options (color, description)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {collectionFeedback && (
                <div
                  style={{
                    marginTop: '6px',
                    padding: '5px 8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid #10b981',
                    borderRadius: '4px',
                    color: '#10b981',
                    fontSize: '0.76rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <i className="fi fi-rr-check" style={{ fontSize: '0.7rem' }} />
                  {collectionFeedback}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '0.74rem',
                  color: 'var(--reader-text-subtle)',
                }}
              >
                <span>
                  {selectedCollectionIds.length === 0
                    ? 'No collection selected (Saved to all subscriptions & inbox)'
                    : selectedCollectionIds.length === 1
                    ? 'Adding feed items to 1 collection'
                    : `Adding feed items to ${selectedCollectionIds.length} collections`}
                </span>
              </div>
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Custom Feed Title (optional)</label>
              <input
                type="text"
                className="reader-input"
                placeholder="Leave blank to use feed's default title"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                disabled={isSubscribing}
              />
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Refresh Interval</label>
              <select
                className="reader-input"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={isSubscribing}
              >
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every 1 hour (Default)</option>
                <option value={360}>Every 6 hours</option>
                <option value={1440}>Every 24 hours</option>
              </select>
            </div>
          </div>

          <div className="reader-modal-footer">
            <button type="button" className="reader-btn" onClick={onClose} disabled={isSubscribing}>
              Cancel
            </button>
            <button
              type="submit"
              className="reader-btn reader-btn-primary"
              disabled={isSubscribing || isDiscovering || !urlInput.trim()}
            >
              {isSubscribing ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
